import classNames from 'classnames';
import React, { FC, PropsWithChildren, ReactNode } from 'react';
import { Control, Controller, RegisterOptions, UseFormRegister } from 'react-hook-form';
import { isForwardRef } from 'react-is';
import { ValuesType } from 'utility-types';

import s from './Field.module.css';

type ValidateCallback = (value: any) => boolean | string;
type WithMessage<T> = T & { message: string };

interface Rules {
  required: WithMessage<{ required?: boolean }>;
  email: WithMessage<{ email?: boolean }>;
  number: WithMessage<{ number?: boolean }>;
  maxLength: WithMessage<{ maxLength?: number }>;
  minLength: WithMessage<{ minLength?: number }>;
  max: WithMessage<{ max?: number }>;
  min: WithMessage<{ min?: number }>;
  pattern: WithMessage<{ pattern?: RegExp }>;
  validate: { validate?: ValidateCallback | Record<string, ValidateCallback> };
}

type Rule = ValuesType<Rules>;

interface Props {
  className?: string;
  label?: ReactNode;
  name: string;
  help?: ReactNode;
  error?: ReactNode;
  register?: UseFormRegister<any>;
  rules?: Rule | Rule[];
  control?: Control;
  shouldUnregister?: boolean;
  defaultValue?: any;
}

const emailRegExp =
  // eslint-disable-next-line no-control-regex
  /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;

const ruleTypes = [
  'required',
  'email',
  'number',
  'maxLength',
  'minLength',
  'max',
  'min',
  'pattern',
  'validate',
] as const;

type RuleType = (typeof ruleTypes)[number];

const htmlControls = ['input', 'textarea', 'select', 'datalist'];

const transformRules = (rules: Rule | Rule[]) => {
  if (!rules) return {};
  const options: RegisterOptions = {};
  (Array.isArray(rules) ? rules : [rules]).forEach((rule) => {
    ruleTypes.forEach((type: RuleType) => {
      if (type in rule) {
        if (type === 'email') {
          options.pattern = {
            value: emailRegExp,
            message: (rule as Rules['email']).message,
          };
        } else if (type === 'validate') {
          options[type] = (rule as Rules['validate'])[type];
        } else {
          options[type as keyof RegisterOptions] = {
            value: rule[type as keyof Rule],
            message: (rule as ValuesType<Omit<Rules, 'validate'>>).message,
          };
        }
      }
    });
  });
  return options;
};

const Field: FC<PropsWithChildren<Props>> = ({
  className,
  label,
  name,
  help,
  error,
  children,
  rules,
  control,
  register,
  shouldUnregister = false,
  defaultValue,
}) => {
  if (React.Children.count(children) > 1) throw Error('Field component must have only one child');

  return (
    <div className={classNames(s.root, { ['field-error']: !!error }, className)}>
      {label && (
        <label htmlFor={name} className={s.label}>
          {label}
        </label>
      )}
      <div className={s.control}>
        {React.Children.map(children as JSX.Element, (child) => {
          if (htmlControls.includes(`${child.type}`) || isForwardRef(child)) {
            return React.createElement(child.type, {
              ...child.props,
              ...register?.(name, {
                ...transformRules(rules || []),
              }),
            });
          }

          return (
            <Controller
              name={name}
              control={control}
              rules={transformRules(rules || [])}
              key={child.props.name}
              shouldUnregister={shouldUnregister}
              defaultValue={defaultValue}
              render={({ field: { onChange, onBlur, value } }) =>
                React.createElement(child.type, {
                  ...child.props,
                  value,
                  defaultValue,
                  onChange,
                  onBlur,
                })
              }
            />
          );
        })}
      </div>
      {!!error && <div className={s.errorMessage}>{error}</div>}
      {!!help && <div className={s.help}>{help}</div>}
    </div>
  );
};

Field.displayName = 'Field';

export default Field;
